/**
 * Early Bird Discount utilities.
 *
 * Discount applied when booking is made X days before the booking start date.
 *
 * Config stored in listing.publicData.earlyBirdDiscount = {
 *   daysInAdvance: number,   — must book at least this many days ahead
 *   discountPercent: number  — percent discount (1-100)
 * }
 */

export const DEFAULT_DAYS_IN_ADVANCE = 7;
export const DEFAULT_DISCOUNT_PERCENT = 10;

/**
 * Get early bird config from a listing.
 * @param {Object|null} listing
 * @returns {{ daysInAdvance: number, discountPercent: number } | null}
 */
export const getEarlyBirdConfig = (listing) => {
  const config = listing?.attributes?.publicData?.earlyBirdDiscount;
  if (!config || !config.daysInAdvance || !config.discountPercent) return null;
  return {
    daysInAdvance: parseInt(config.daysInAdvance, 10),
    discountPercent: parseFloat(config.discountPercent),
  };
};

/**
 * Check if early bird discount applies.
 * @param {string|Date} bookingStart — booking start date
 * @param {Object} config — { daysInAdvance }
 * @param {Date} [now] — current date (for testing)
 * @returns {boolean}
 */
export const isEarlyBird = (bookingStart, config, now = new Date()) => {
  if (!config || !bookingStart) return false;
  const start = new Date(bookingStart);
  const daysUntil = (start.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return daysUntil >= config.daysInAdvance;
};

/**
 * Calculate early bird discount.
 * @param {number} priceCents
 * @param {number} discountPercent
 * @returns {{ discountCents: number, finalPriceCents: number }}
 */
export const calculateEarlyBirdDiscount = (priceCents, discountPercent) => {
  const discountCents = Math.round(priceCents * (discountPercent / 100));
  return { discountCents, finalPriceCents: priceCents - discountCents };
};

/**
 * Validate early bird config.
 * @param {{ daysInAdvance, discountPercent }} config
 * @returns {string|null} — error or null
 */
export const validateEarlyBirdConfig = ({ daysInAdvance, discountPercent }) => {
  if (!daysInAdvance || daysInAdvance < 1) return 'Days in advance must be at least 1';
  if (!discountPercent || discountPercent < 1 || discountPercent > 100) return 'Discount must be 1-100%';
  return null;
};
